import React, { useEffect, useState } from 'react';
import { CitationData } from 'types/AssistantTypes';
import { getKbDocument, downloadKbDocumentFile } from '@dev-streetbeat/chat';
import type { KbDocument } from '@dev-streetbeat/chat/types';
import { useChatConfig } from '@dev-streetbeat/chat/react';
import { DownloadIcon } from 'lucide-react';
import styles from './citationArtifact.module.scss';
import { createLogger } from 'shared/logger';

const log = createLogger('Citation');

type Props = {
  data: CitationData;
};

export const CitationArtifact: React.FC<Props> = ({ data }) => {
  const { index, page_num, document_id, file_id } = data;
  const [documentData, setDocumentData] = useState<KbDocument | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { config } = useChatConfig();

  useEffect(() => {
    const fetchDocumentData = async () => {
      if (!document_id) return;

      try {
        const docData = await getKbDocument(document_id, config);
        setDocumentData(docData);
      } catch (error) {
        log.error('fetch document data failed', { error });
      }
    };

    fetchDocumentData();
  }, [document_id, config]);

  const handleDownload = async () => {
    if (!document_id || !file_id) return;

    try {
      setIsDownloading(true);
      const { blob } = await downloadKbDocumentFile(document_id, file_id, config);
      const filename = documentData?.title ? `${documentData.title}.pdf` : `document-${document_id}.pdf`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      
      URL.revokeObjectURL(link.href);
    } catch (error) {
      log.error('download document failed', { error });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.citationContainer}>
      <div className={styles.citationBadge}>
        <div className={styles.citationContent}>
          <span className={styles.citationNumber}>[{index}]</span>
          <div className={styles.citationDetails}>
            {documentData?.title && (
              <span className={styles.citationTitle}>{documentData.title}</span>
            )}
            <span className={styles.citationInfo}>Page {page_num}</span>
          </div>
        </div>
        <button
          className={styles.downloadButton}
          onClick={handleDownload}
          disabled={isDownloading || !file_id}
          title="Download document"
        >
          <DownloadIcon className={styles.downloadIcon} />
        </button>
      </div>
    </div>
  );
};
